import { useState, useRef, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, Phone, ChevronDown, LayoutDashboard, Heart, User as UserIcon, LogOut, Bookmark } from "lucide-react";
import { LOGO_URL } from "../lib/api";
import { EDGE_APP_URL } from "../lib/edge";
import { useAuth } from "../lib/auth";
import { useFavorites } from "../lib/favorites";

const mainLinks = [
  { to: "/", label: "Home" },
  { to: "/properties", label: "Properties" },
  { to: "/neighborhoods", label: "Neighborhoods" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

const mlsLinks = [
  { to: "/mls-listings", label: "MLS Listings (Live)" },
  { to: "/mls-map", label: "MLS Map" },
  { to: "/map-search", label: "Neighborhood Map" },
  { to: "/mls-search", label: "HAR IDX Search" },
  { to: "/quick-search", label: "Quick Search" },
  { to: "/foreclosures", label: "Foreclosures" },
  { to: "/home-value", label: "Home Value Estimator" },
  { to: "/schools", label: "Schools" },
  { to: "/har-neighborhoods", label: "HAR Neighborhoods" },
];

const mortgageLinks = [
  { to: "/mortgage", label: "Mortgage Overview" },
  { to: "/loan-programs", label: "Loan Programs" },
  { to: "/pre-approval", label: "Get Pre-Approved" },
  { to: "/refinance", label: "Refinance" },
  { to: "/dscr-calculator", label: "DSCR Calculator" },
  { to: "/loan-officers", label: "Loan Officer" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [mlsOpen, setMlsOpen] = useState(false);
  const [mortgageOpen, setMortgageOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const navigate = useNavigate();
  const { user, login, logout } = useAuth();
  const { keys } = useFavorites();
  const savedCount = keys.size;
  const mlsRef = useRef(null);
  const mortRef = useRef(null);
  const profileRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (mlsRef.current && !mlsRef.current.contains(e.target)) setMlsOpen(false);
      if (mortRef.current && !mortRef.current.contains(e.target)) setMortgageOpen(false);
      if (profileRef.current && !profileRef.current.contains(e.target)) setProfileOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <header
      className="sticky top-0 z-40 w-full bg-white/85 backdrop-blur-xl border-b border-gray-100"
      data-testid="site-navbar"
    >
      <nav className="max-w-7xl mx-auto px-6 lg:px-10 h-28 flex items-center justify-between">
        <Link
          to="/"
          className="flex items-center gap-3"
          data-testid="navbar-logo-link"
          onClick={() => setOpen(false)}
        >
          <img
            src={LOGO_URL}
            alt="Gens Realty"
            className="h-20 w-auto object-contain"
          />
          <div className="eyebrow text-gray-500 hidden sm:block border-l border-gray-200 pl-3">
            Houston, TX
          </div>
        </Link>

        <ul className="hidden xl:flex items-center gap-7">
          {mainLinks.slice(0, 3).map((l) => (
            <li key={l.to}>
              <NavLink
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `text-sm font-medium tracking-wide transition-colors hover:text-[#E63946] ${
                    isActive ? "text-[#E63946]" : "text-[#1A1A1A]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            </li>
          ))}

          {/* MLS dropdown */}
          <li className="relative" ref={mlsRef}>
            <button
              onClick={() => { setMlsOpen((v) => !v); setMortgageOpen(false); }}
              className="flex items-center gap-1 text-sm font-medium text-[#1A1A1A] hover:text-[#E63946]"
              data-testid="nav-mls-dropdown-trigger"
            >
              MLS <ChevronDown className={`w-3.5 h-3.5 transition-transform ${mlsOpen ? "rotate-180" : ""}`} />
            </button>
            {mlsOpen && (
              <div className="absolute top-full left-0 mt-3 w-60 bg-white border border-gray-100 rounded-xl shadow-xl py-2">
                {mlsLinks.map((l) => (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    onClick={() => setMlsOpen(false)}
                    className="block px-5 py-2.5 text-sm text-[#1A1A1A] hover:bg-stone-50 hover:text-[#E63946]"
                    data-testid={`nav-mls-${l.to.slice(1)}`}
                  >
                    {l.label}
                  </NavLink>
                ))}
              </div>
            )}
          </li>

          {/* Mortgage dropdown */}
          <li className="relative" ref={mortRef}>
            <button
              onClick={() => { setMortgageOpen((v) => !v); setMlsOpen(false); }}
              className="flex items-center gap-1 text-sm font-medium text-[#1A1A1A] hover:text-[#E63946]"
              data-testid="nav-mortgage-dropdown-trigger"
            >
              Mortgage <ChevronDown className={`w-3.5 h-3.5 transition-transform ${mortgageOpen ? "rotate-180" : ""}`} />
            </button>
            {mortgageOpen && (
              <div className="absolute top-full left-0 mt-3 w-64 bg-white border border-gray-100 rounded-xl shadow-xl py-2">
                <div className="px-5 pt-2 pb-3 border-b border-gray-100">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500">
                    Edge Home Finance
                  </div>
                  <div className="text-xs text-gray-400 mt-0.5">NMLS #891464 · Pierre Joseph #1978260</div>
                </div>
                {mortgageLinks.map((l) => (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    onClick={() => setMortgageOpen(false)}
                    className="block px-5 py-2.5 text-sm text-[#1A1A1A] hover:bg-stone-50 hover:text-[#E63946]"
                    data-testid={`nav-mortgage-${l.to.slice(1)}`}
                  >
                    {l.label}
                  </NavLink>
                ))}
              </div>
            )}
          </li>

          {mainLinks.slice(3).map((l) => (
            <li key={l.to}>
              <NavLink
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `text-sm font-medium tracking-wide transition-colors hover:text-[#E63946] ${
                    isActive ? "text-[#E63946]" : "text-[#1A1A1A]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            </li>
          ))}
        </ul>

        <div className="hidden xl:flex items-center gap-3">
          {user && (
            <Link
              to="/saved-listings"
              className="relative flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-[#1A1A1A] hover:text-[#E63946]"
              data-testid="nav-saved-link"
            >
              <Heart className="w-3.5 h-3.5" strokeWidth={2} />
              Saved
              {savedCount > 0 && (
                <span className="ml-0.5 bg-[#E63946] text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center leading-none">
                  {savedCount}
                </span>
              )}
            </Link>
          )}
          {user?.is_admin && (
            <Link
              to="/admin"
              className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-[#E63946] hover:underline"
              data-testid="nav-admin-link"
            >
              <LayoutDashboard className="w-3.5 h-3.5" /> Admin
            </Link>
          )}
          <a
            href="tel:8327414662"
            className="flex items-center gap-2 text-sm text-[#1A1A1A] hover:text-[#E63946] transition-colors"
            data-testid="navbar-phone-link"
          >
            <Phone className="w-4 h-4" strokeWidth={1.75} />
            (832) 741-4662
          </a>

          {/* Auth surface — Sign In / Create Account for guests, profile menu when logged in */}
          {!user ? (
            <div className="flex items-center gap-2">
              <button
                onClick={login}
                className="text-xs font-semibold uppercase tracking-[0.15em] text-[#1A1A1A] hover:text-[#E63946] px-3 py-2 transition-colors"
                data-testid="navbar-signin-btn"
              >
                Sign In
              </button>
              <button
                onClick={login}
                className="border border-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-white text-[#1A1A1A] px-4 py-2 rounded-md text-xs font-semibold uppercase tracking-[0.12em] transition-colors"
                data-testid="navbar-signup-btn"
              >
                Create Account
              </button>
            </div>
          ) : (
            <div className="relative" ref={profileRef}>
              <button
                onClick={() => setProfileOpen((v) => !v)}
                className="flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-full border border-gray-200 hover:border-[#E63946] transition-colors"
                data-testid="navbar-profile-btn"
              >
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-7 h-7 rounded-full object-cover" />
                ) : (
                  <div className="w-7 h-7 rounded-full bg-[#E63946] text-white flex items-center justify-center text-[11px] font-bold">
                    {(user.name || user.email || "?").charAt(0).toUpperCase()}
                  </div>
                )}
                <span className="text-xs font-semibold text-[#1A1A1A] max-w-[110px] truncate">
                  {user.name?.split(" ")[0] || user.email?.split("@")[0]}
                </span>
                <ChevronDown className={`w-3 h-3 text-gray-400 transition-transform ${profileOpen ? "rotate-180" : ""}`} />
              </button>
              {profileOpen && (
                <div className="absolute top-full right-0 mt-2 w-64 bg-white border border-gray-100 rounded-xl shadow-xl py-2 z-50">
                  <div className="px-5 py-3 border-b border-gray-100">
                    <div className="text-sm font-semibold text-[#1A1A1A] truncate">{user.name || "Signed in"}</div>
                    <div className="text-xs text-gray-500 truncate">{user.email}</div>
                  </div>
                  <Link
                    to="/saved-listings"
                    onClick={() => setProfileOpen(false)}
                    className="flex items-center justify-between px-5 py-2.5 text-sm text-[#1A1A1A] hover:bg-stone-50"
                    data-testid="profile-menu-saved"
                  >
                    <span className="flex items-center gap-2">
                      <Heart className="w-4 h-4 text-[#E63946]" /> Saved Homes
                    </span>
                    {savedCount > 0 && (
                      <span className="bg-stone-100 text-[#1A1A1A] text-[10px] font-bold rounded-full min-w-[20px] h-[20px] px-1.5 flex items-center justify-center">
                        {savedCount}
                      </span>
                    )}
                  </Link>
                  <Link
                    to="/saved-searches"
                    onClick={() => setProfileOpen(false)}
                    className="flex items-center gap-2 px-5 py-2.5 text-sm text-[#1A1A1A] hover:bg-stone-50"
                    data-testid="profile-menu-saved-searches"
                  >
                    <Bookmark className="w-4 h-4 text-[#E63946]" /> Saved Searches
                  </Link>
                  {user.is_admin && (
                    <Link
                      to="/admin"
                      onClick={() => setProfileOpen(false)}
                      className="flex items-center gap-2 px-5 py-2.5 text-sm text-[#1A1A1A] hover:bg-stone-50"
                      data-testid="profile-menu-admin"
                    >
                      <LayoutDashboard className="w-4 h-4 text-[#E63946]" /> Admin Dashboard
                    </Link>
                  )}
                  <button
                    onClick={async () => {
                      setProfileOpen(false);
                      await logout();
                      navigate("/");
                    }}
                    className="w-full flex items-center gap-2 px-5 py-2.5 text-sm text-[#1A1A1A] hover:bg-stone-50 border-t border-gray-100 mt-1 pt-3"
                    data-testid="profile-menu-logout"
                  >
                    <LogOut className="w-4 h-4 text-gray-400" /> Sign out
                  </button>
                </div>
              )}
            </div>
          )}

          <button
            onClick={() => window.open(EDGE_APP_URL, "_blank", "noopener,noreferrer")}
            className="bg-[#E63946] hover:bg-[#c9313d] text-white px-5 py-2.5 rounded-md text-sm font-semibold transition-colors"
            data-testid="navbar-cta-button"
          >
            Apply Online
          </button>
        </div>

        <button
          onClick={() => setOpen((v) => !v)}
          className="xl:hidden text-[#1A1A1A]"
          aria-label="Toggle menu"
          data-testid="navbar-mobile-toggle"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </nav>

      {open && (
        <div className="xl:hidden border-t border-gray-100 bg-white max-h-[80vh] overflow-y-auto">
          <ul className="flex flex-col px-6 py-4 gap-1">
            {mainLinks.map((l) => (
              <li key={l.to}>
                <NavLink
                  to={l.to}
                  end={l.to === "/"}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `block py-2 text-base font-medium ${
                      isActive ? "text-[#E63946]" : "text-[#1A1A1A]"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              </li>
            ))}
            <li className="pt-2 mt-2 border-t border-gray-100">
              <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-2">
                HAR · MLS
              </div>
              {mlsLinks.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  className="block py-2 text-sm text-[#333]"
                >
                  {l.label}
                </NavLink>
              ))}
            </li>
            <li className="pt-2 mt-2 border-t border-gray-100">
              <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-2">
                Edge Home Finance · Mortgage
              </div>
              {mortgageLinks.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  className="block py-2 text-sm text-[#333]"
                >
                  {l.label}
                </NavLink>
              ))}
            </li>
            {user && (
              <li className="pt-2 mt-2 border-t border-gray-100">
                <Link
                  to="/saved-listings"
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-2 py-2 text-sm font-semibold text-[#1A1A1A]"
                  data-testid="nav-mobile-saved-link"
                >
                  <Heart className="w-4 h-4" strokeWidth={2} />
                  Saved Homes
                  {savedCount > 0 && (
                    <span className="bg-[#E63946] text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center leading-none">
                      {savedCount}
                    </span>
                  )}
                </Link>
              </li>
            )}
            {user?.is_admin && (
              <li className="pt-2 mt-2 border-t border-gray-100">
                <Link
                  to="/admin"
                  onClick={() => setOpen(false)}
                  className="block py-2 text-sm font-semibold text-[#E63946]"
                >
                  Admin Dashboard
                </Link>
              </li>
            )}
            <li className="pt-3 flex flex-col gap-2 border-t border-gray-100 mt-2">
              {!user ? (
                <>
                  <button
                    onClick={() => { setOpen(false); login(); }}
                    className="w-full border border-[#1A1A1A] text-[#1A1A1A] py-3 rounded-md text-sm font-semibold uppercase tracking-[0.12em]"
                    data-testid="navbar-mobile-signin"
                  >
                    Sign In
                  </button>
                  <button
                    onClick={() => { setOpen(false); login(); }}
                    className="w-full bg-[#1A1A1A] text-white py-3 rounded-md text-sm font-semibold uppercase tracking-[0.12em]"
                    data-testid="navbar-mobile-signup"
                  >
                    Create Account
                  </button>
                </>
              ) : (
                <button
                  onClick={async () => { setOpen(false); await logout(); navigate("/"); }}
                  className="w-full border border-gray-300 text-gray-600 py-3 rounded-md text-sm font-semibold flex items-center justify-center gap-2"
                  data-testid="navbar-mobile-logout"
                >
                  <LogOut className="w-4 h-4" /> Sign out
                </button>
              )}
              <button
                onClick={() => {
                  setOpen(false);
                  window.open(EDGE_APP_URL, "_blank", "noopener,noreferrer");
                }}
                className="w-full bg-[#E63946] hover:bg-[#c9313d] text-white py-3 rounded-md text-sm font-semibold"
                data-testid="navbar-mobile-cta"
              >
                Apply Online
              </button>
            </li>
          </ul>
        </div>
      )}
    </header>
  );
}
